/**
 * DashboardV2Layout — Phase 2.2.
 *
 * Renders the four new v2.0 surfaces in a clean BI-style grid:
 *   - Header chip (Org Name + Logo, "Array Engine · Dashboard")
 *   - 8-KPI Intelligence Grid (Row 1 fixed + Row 2 customisable)
 *   - Quick Actions panel (Customize · Add Widgets · Share · Smart Report)
 *   - Operations Feed (Recent Programs / Data Activity tabs)
 *   - Upcoming Tasks calendar (priority-sorted list)
 *
 * The parent (LiveDashboard) keeps the existing Filters/Scorecards/
 * Charts/DQ surface; this component sits at the top to deliver the
 * v2 visible upgrade without ripping out what already works.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sliders,
  PlusSquare,
  Share2,
  FileDown,
  Building2,
  FolderKanban,
  Activity,
  CalendarClock,
  ChevronRight,
  Check,
  X,
  Sparkles,
  Loader2,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  useWidgetGallery,
  WidgetGalleryDialog,
  HeatMapWidget,
  GeoMapWidget,
} from "./WidgetGallery";

const STATUS_CHIP = {
  healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  needs_push: "bg-amber-50 text-amber-800 border-amber-200",
  at_risk: "bg-rose-50 text-rose-700 border-rose-200",
  stale: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = {
  healthy: "On Track",
  needs_push: "Needs Push",
  at_risk: "At Risk",
  stale: "Stale",
};
const PRIORITY_CHIP = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function DashboardV2Layout({ selPrograms, children, onKpiClick }) {
  const [grid, setGrid] = useState(null);
  const [ops, setOps] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [pinned, setPinned] = useState(() => {
    // Pinned indicator IDs persist in localStorage so the Upload Wizard's
    // "Show on Dashboard" checkboxes survive a navigation and surface in
    // Row 2 the next time the user lands on the dashboard.
    try {
      const raw = localStorage.getItem("ae-dashboard-pinned-v1");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [widgetGalleryOpen, setWidgetGalleryOpen] = useState(false);
  const [activeWidgets, setActiveWidgets] = useWidgetGallery();
  const [smartReportOpen, setSmartReportOpen] = useState(false);
  const [opsTab, setOpsTab] = useState("programs");
  // Phase 3.31 — Lazy Discovery banner. Dismiss persists per-browser so
  // the hint doesn't keep nagging the user after they've seen it once.
  const [lazyDiscoveryDismissed, setLazyDiscoveryDismissed] = useState(() => {
    try { return localStorage.getItem("ae-lazy-discovery-dismissed-v1") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    api.get("/dashboard/v2/operations-feed").then(({ data }) => setOps(data)).catch(() => {});
    api.get("/dashboard/v2/upcoming-tasks").then(({ data }) => setTasks(data)).catch(() => {});
  }, []);

  // Refetch AI Insights whenever the program scope changes (Claude
  // round-trip is ~5-10s; the card has its own skeleton so it doesn't
  // block the rest of the grid).
  useEffect(() => {
    setInsightsLoading(true);
    api.post("/dashboard/v2/ai-insights", {
      program_ids: selPrograms?.length ? selPrograms : null,
    })
      .then(({ data }) => setInsights(data))
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  }, [selPrograms]);

  useEffect(() => {
    api.post("/dashboard/v2/grid", {
      program_ids: selPrograms?.length ? selPrograms : null,
      pinned_indicator_ids: pinned.length ? pinned : null,
    })
      .then(({ data }) => setGrid(data))
      .catch(() => {});
  }, [selPrograms, pinned]);

  return (
    <div className="space-y-6" data-testid="dashboard-v2-layout">
      {/* ---------- Phase 3.31 — Lazy Discovery hint ---------- */}
      {grid?.auto_discovered && !lazyDiscoveryDismissed && (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-3"
          data-testid="lazy-discovery-banner"
        >
          <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900">Auto-discovered from your data</p>
            <p className="text-xs text-emerald-800/80 mt-0.5">
              We&apos;ve pinned the top {grid.row2.filter((c) => c.indicator_id).length} numeric indicators across your accessible programs.
              Open <span className="font-medium">Customize KPIs</span> any time to swap them.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setCustomizeOpen(true)}
              className="text-xs font-medium text-emerald-800 hover:text-emerald-900 px-2 py-1 rounded-sm hover:bg-emerald-100"
              data-testid="lazy-discovery-customize"
            >
              Customize
            </button>
            <button
              type="button"
              onClick={() => {
                setLazyDiscoveryDismissed(true);
                try { localStorage.setItem("ae-lazy-discovery-dismissed-v1", "1"); } catch { /* ignore */ }
              }}
              className="text-xs text-emerald-700 hover:text-emerald-900 p-1 rounded-sm hover:bg-emerald-100"
              data-testid="lazy-discovery-dismiss"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ---------- Layer 1 : 8-KPI Intelligence Grid ---------- */}
      {grid && (
        <IntelligenceGrid
          row1={grid.row1}
          row2={grid.row2}
          onCustomize={() => setCustomizeOpen(true)}
          onCardClick={onKpiClick}
        />
      )}

      {/* ---------- Layer 2 : Performance (4 columns — Line · Bar · Pie · Quick Actions) ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-layer-2">
        <div data-testid="layer-2-trend" className="min-w-0">
          {children}
        </div>
        <div data-testid="layer-2-bar" className="min-w-0">
          <IndicatorBarChartCard programIds={selPrograms} />
        </div>
        <div data-testid="layer-2-pie" className="min-w-0">
          <DistributionPieChartCard programIds={selPrograms} />
        </div>
        <div data-testid="layer-2-quick-actions">
          <QuickActionsPanel
            onCustomize={() => setCustomizeOpen(true)}
            onShare={() => setShareOpen(true)}
            onWidgets={() => setWidgetGalleryOpen(true)}
            onSmartReport={() => setSmartReportOpen(true)}
          />
        </div>
      </div>

      {/* Optional widget grid — opt-in extras only (Heat Map, Geo Map).
          Gender Donut & Target vs Achievement were removed from the
          gallery in Phase 3.27; their data now lives in the always-on
          Layer 2 Bar + Pie charts. */}
      {(activeWidgets.heatmap || activeWidgets.geo_map) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="v2-widget-grid">
          {activeWidgets.heatmap && (
            <div className="lg:col-span-2">
              <HeatMapWidget programIds={selPrograms} />
            </div>
          )}
          {activeWidgets.geo_map && (
            <div className="lg:col-span-2">
              <GeoMapWidget programIds={selPrograms} />
            </div>
          )}
        </div>
      )}

      {/* ---------- Layer 3 : Operations (Feed · Tasks · AI Insights) ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="dashboard-layer-3">
        <div className="lg:col-span-5">
          <OperationsFeed ops={ops} tab={opsTab} setTab={setOpsTab} />
        </div>
        <div className="lg:col-span-4">
          <UpcomingTasks tasks={tasks} />
        </div>
        <div className="lg:col-span-3">
          <AIInsightsCard insights={insights} loading={insightsLoading} />
        </div>
      </div>

      <CustomizeKpiDialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        picker={grid?.indicator_picker || []}
        selected={pinned.length ? pinned : (grid?.pinned_indicator_ids || [])}
        onSave={(ids) => {
          setPinned(ids);
          try { localStorage.setItem("ae-dashboard-pinned-v1", JSON.stringify(ids)); } catch {/* ignore */}
          setCustomizeOpen(false);
          toast.success(`${ids.length} indicator cards saved.`);
        }}
      />
      <ShareDashboardDialog open={shareOpen} onClose={() => setShareOpen(false)} selPrograms={selPrograms} pinned={pinned} />
      <WidgetGalleryDialog
        open={widgetGalleryOpen}
        onClose={() => setWidgetGalleryOpen(false)}
        active={activeWidgets}
        onChange={setActiveWidgets}
      />
      <SmartReportDialog
        open={smartReportOpen}
        onClose={() => setSmartReportOpen(false)}
        selPrograms={selPrograms}
        pinned={pinned}
        activeWidgets={activeWidgets}
      />
    </div>
  );
}

function IntelligenceGrid({ row1, row2, onCustomize, onCardClick }) {
  return (
    <div className="space-y-3" data-testid="v2-intelligence-grid">
      <div className="flex items-center justify-between">
        <p className="label-overline">8-KPI Intelligence Grid</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="v2-row1">
        {row1.map((c, idx) => (
          <KpiCard key={c.key} card={c} row={1} index={idx} onClick={onCardClick} />
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="v2-row2">
        {row2.map((c, idx) => c.empty ? (
          <button
            key={c.key}
            type="button"
            onClick={onCustomize}
            className="border border-dashed border-slate-300 rounded-md p-4 text-center text-slate-500 hover:border-emerald-500 hover:text-emerald-700 transition-colors min-h-[120px] flex flex-col items-center justify-center"
            data-testid={`v2-row2-empty-${c.key}`}
          >
            <PlusSquare className="w-4 h-4 mb-1" />
            <span className="text-xs font-medium">Pin an indicator</span>
          </button>
        ) : (
          <KpiCard key={c.key} card={c} row={2} index={idx} onClick={onCardClick} />
        ))}
      </div>
    </div>
  );
}

// ----- Phase 3.29 — "Pop" color scheme for the 8-KPI grid -----
// Row 1 cards are addressed by their fixed system key. Row 2 cards
// rotate through a 4-step palette by index so any pinned indicator
// inherits a predictable colour regardless of which one the user
// chooses. Tones are deliberately soft to keep the chart layer below
// visually distinct and avoid "the dashboard ate my eyes" syndrome.
const ROW1_POP = {
  beneficiaries: {
    container: "bg-slate-900 text-white border-slate-900",
    label: "text-slate-300",
    value: "text-white",
    target: "text-slate-400",
    sub: "text-slate-400",
    statusFallback: "bg-emerald-500 text-slate-900 border-emerald-400",
    track: "bg-slate-700",
    barOk: "bg-emerald-400",
    barBehind: "bg-amber-400",
    varianceOk: "text-emerald-300",
    varianceBehind: "text-amber-300",
  },
  // programs_health uses a status-adaptive palette below
  submission_rate: {
    container: "bg-sky-50 text-slate-900 border-sky-200",
    label: "text-sky-700",
    value: "text-slate-900",
    target: "text-slate-500",
    sub: "text-slate-600",
    track: "bg-sky-200/70",
    barOk: "bg-emerald-500",
    barBehind: "bg-amber-500",
    varianceOk: "text-emerald-700",
    varianceBehind: "text-amber-700",
  },
  completion_rate: {
    container: "bg-indigo-50 text-slate-900 border-indigo-200",
    label: "text-indigo-700",
    value: "text-slate-900",
    target: "text-slate-500",
    sub: "text-slate-600",
    track: "bg-indigo-200/70",
    barOk: "bg-emerald-500",
    barBehind: "bg-amber-500",
    varianceOk: "text-emerald-700",
    varianceBehind: "text-amber-700",
  },
};

const PROGRAMS_HEALTH_POP = {
  healthy:    { container: "bg-emerald-50 text-slate-900 border-emerald-200", label: "text-emerald-700", value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-emerald-200/70", barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  at_risk:    { container: "bg-amber-50  text-slate-900 border-amber-200",   label: "text-amber-800",   value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-amber-200/70",   barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  needs_push: { container: "bg-amber-50  text-slate-900 border-amber-200",   label: "text-amber-800",   value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-amber-200/70",   barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  stale:      { container: "bg-slate-50  text-slate-900 border-slate-200",   label: "text-slate-600",   value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-slate-200",      barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
};

const ROW2_POP = [
  // Card 1 — Teal
  { container: "bg-teal-50  text-slate-900 border-teal-200",     label: "text-teal-700",     value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-teal-200/70",  barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  // Card 2 — Rose
  { container: "bg-rose-50  text-slate-900 border-rose-200",     label: "text-rose-700",     value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-rose-200/70",  barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  // Card 3 — Mint (soft green, distinct from emerald above)
  { container: "bg-lime-50  text-slate-900 border-lime-200",     label: "text-lime-700",     value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-lime-200/70",  barOk: "bg-emerald-500", barBehind: "bg-amber-500", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
  // Card 4 — Amber/Gold
  { container: "bg-amber-50 text-slate-900 border-amber-200",    label: "text-amber-800",    value: "text-slate-900", target: "text-slate-500", sub: "text-slate-600", track: "bg-amber-200/70", barOk: "bg-emerald-500", barBehind: "bg-amber-600", varianceOk: "text-emerald-700", varianceBehind: "text-amber-700" },
];

function _popPalette(row, index, card) {
  if (row === 1) {
    if (card.key === "programs_health") {
      return PROGRAMS_HEALTH_POP[card.status] || PROGRAMS_HEALTH_POP.stale;
    }
    return ROW1_POP[card.key] || ROW1_POP.submission_rate;
  }
  return ROW2_POP[index % ROW2_POP.length];
}

function KpiCard({ card, row = 1, index = 0, onClick }) {
  const chip = STATUS_CHIP[card.status] || STATUS_CHIP.stale;
  const palette = _popPalette(row, index, card);
  const clickable = typeof onClick === "function";
  const handle = clickable ? () => onClick(card) : undefined;
  return (
    <div
      className={`rounded-md border p-4 transition-all ${palette.container} ${
        clickable ? "cursor-pointer hover:shadow-md" : "hover:shadow-sm"
      }`}
      onClick={handle}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handle(); } } : undefined}
      data-testid={`v2-kpi-${card.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`label-overline truncate ${palette.label}`}>{card.label}</p>
        <span
          className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-sm border ${palette.statusFallback || chip}`}
          data-testid={`v2-kpi-${card.key}-status`}
        >
          {STATUS_LABEL[card.status] || card.status}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={`font-heading text-3xl font-semibold ${palette.value}`} data-testid={`v2-kpi-${card.key}-current`}>
          {card.current_display}
        </span>
        {card.target_display && (
          <span className={`text-xs ${palette.target}`}>/ {card.target_display}</span>
        )}
      </div>
      {card.target_display && typeof card.variance_pct === "number" && (
        <TargetProgress card={card} palette={palette} />
      )}
      {card.trend_label && (
        <p className={`text-[11px] mt-1.5 ${palette.sub}`}>{card.trend_label}</p>
      )}
      <p className={`text-[11px] mt-1.5 leading-snug line-clamp-2 ${palette.sub}`}>{card.subtext}</p>
    </div>
  );
}

// Phase 3.45 — thin progress bar + variance label for KPIs that have a
// target defined. Bar fill = clamp(100 + variance, 0, 100). Over-target
// is signalled with the small "+X% over" pill in emerald next to the bar.
function TargetProgress({ card, palette }) {
  const variance = Number(card.variance_pct);
  const pctOfTarget = Math.max(0, 100 + variance);
  const fill = Math.min(pctOfTarget, 100);
  const ahead = variance >= 0;
  const barColor = ahead ? palette.barOk : palette.barBehind;
  const labelColor = ahead ? palette.varianceOk : palette.varianceBehind;
  const sign = variance > 0 ? "+" : "";
  const text = ahead
    ? `${sign}${variance.toFixed(variance % 1 === 0 ? 0 : 1)}% over target`
    : `${variance.toFixed(variance % 1 === 0 ? 0 : 1)}% behind`;
  return (
    <div className="mt-2.5" data-testid={`v2-kpi-${card.key}-progress`}>
      <div
        className={`h-1 w-full rounded-full overflow-hidden ${palette.track}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pctOfTarget)}
        aria-label={`${card.label} — ${Math.round(pctOfTarget)}% of target`}
      >
        <div
          className={`h-full ${barColor} transition-all duration-500 ease-out`}
          style={{ width: `${fill}%` }}
        />
      </div>
      <p
        className={`text-[10px] font-medium mt-1 ${labelColor}`}
        data-testid={`v2-kpi-${card.key}-variance`}
      >
        {text}
      </p>
    </div>
  );
}

function AIInsightsCard({ insights, loading }) {
  const items = insights?.insights || [];
  const isFallback = insights?.fallback;
  const KIND_TONE = {
    hotspot: { ring: "border-rose-200 bg-rose-50/40", text: "text-rose-700", label: "Hotspot" },
    quick_win: { ring: "border-emerald-200 bg-emerald-50/40", text: "text-emerald-700", label: "Quick win" },
    action: { ring: "border-slate-200 bg-slate-50/40", text: "text-slate-700", label: "Action" },
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 h-full" data-testid="v2-ai-insights-card">
      <div className="flex items-center justify-between mb-2">
        <p className="label-overline flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
          AI Insights
          {!loading && insights && !isFallback && (
            <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm font-medium">
              Claude
            </span>
          )}
          {!loading && isFallback && (
            <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-sm font-medium">
              Fallback
            </span>
          )}
        </p>
      </div>
      {loading && !items.length ? (
        <div className="space-y-2.5" data-testid="v2-ai-insights-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-slate-200 rounded-sm p-2.5 space-y-1.5 bg-white">
              <div className="h-2.5 bg-slate-100 rounded-sm w-1/3 animate-pulse" />
              <div className="h-2.5 bg-slate-100 rounded-sm w-3/4 animate-pulse" />
              <div className="h-2.5 bg-slate-100 rounded-sm w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1" data-testid="v2-ai-insights-list">
          {items.map((it, idx) => {
            const tone = KIND_TONE[it.kind] || KIND_TONE.action;
            return (
              <div
                key={`${it.kind}-${idx}`}
                className={`rounded-sm border p-2.5 ${tone.ring}`}
                data-testid={`v2-ai-insight-${it.kind}`}
              >
                <p className={`text-[10px] uppercase tracking-wider font-semibold ${tone.text}`}>
                  {tone.label}
                </p>
                <p className="font-heading text-xs font-medium text-slate-900 mt-1 leading-snug">
                  {it.title}
                </p>
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed line-clamp-3">
                  {it.detail}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QuickActionsPanel({ onCustomize, onShare, onWidgets, onSmartReport }) {
  const actions = [
    { key: "customize", icon: Sliders, label: "Customize KPIs", onClick: onCustomize },
    { key: "widgets", icon: PlusSquare, label: "Add widgets", onClick: onWidgets },
    { key: "share", icon: Share2, label: "Share dashboard", onClick: onShare },
    { key: "smart-report", icon: FileDown, label: "Smart report", onClick: onSmartReport },
  ];
  return (
    <div className="bg-slate-900 text-slate-100 rounded-sm p-4 h-full" data-testid="v2-quick-actions">
      <p className="label-overline !text-emerald-300 mb-3">Quick actions</p>
      <div className="flex flex-col gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.key}
              type="button"
              onClick={a.onClick}
              className="bg-slate-800 hover:bg-emerald-500 hover:text-slate-900 text-slate-100 transition-colors rounded-sm px-3 py-2 text-left group flex items-center gap-2"
              data-testid={`v2-action-${a.key}`}
            >
              <Icon className="w-4 h-4 text-emerald-400 group-hover:text-slate-900 shrink-0" />
              <div className="text-xs font-medium leading-snug">{a.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OperationsFeed({ ops, tab, setTab }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="v2-operations-feed">
      <div className="flex items-center justify-between">
        <p className="label-overline flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5 text-emerald-600" /> Operations feed
        </p>
        <div className="inline-flex p-0.5 bg-slate-100 rounded-sm border border-slate-200" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "programs"}
            onClick={() => setTab("programs")}
            className={`text-[11px] px-2 py-0.5 rounded-sm font-medium ${
              tab === "programs" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid="v2-ops-tab-programs"
          >
            Recent programs
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "activity"}
            onClick={() => setTab("activity")}
            className={`text-[11px] px-2 py-0.5 rounded-sm font-medium ${
              tab === "activity" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid="v2-ops-tab-activity"
          >
            Data activity
          </button>
        </div>
      </div>
      {!ops ? (
        <div className="mt-3 space-y-2" data-testid="v2-ops-loading">
          <div className="h-3 bg-slate-100 rounded-sm w-3/4 animate-pulse" />
          <div className="h-3 bg-slate-100 rounded-sm w-1/2 animate-pulse" />
        </div>
      ) : tab === "programs" ? (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="v2-ops-programs-list">
          {(ops.recent_programs || []).slice(0, 6).map((p) => (
            <li key={p.id} className="py-2 flex items-center gap-3 text-sm">
              <FolderKanban className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to={`/programs/${p.id}`} className="text-slate-900 font-medium hover:text-emerald-700 truncate flex-1" data-testid={`v2-ops-program-${p.id}`}>
                {p.name}
              </Link>
              <span className="text-[11px] text-slate-500 shrink-0">
                {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
              </span>
              <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
            </li>
          ))}
          {(ops.recent_programs || []).length === 0 && (
            <p className="text-xs text-slate-500 py-3">No programs yet.</p>
          )}
        </ul>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="v2-ops-activity-list">
          {(ops.recent_activity || []).slice(0, 8).map((u) => (
            <li key={u.id} className="py-2 flex items-center gap-3 text-sm">
              <Activity className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-slate-900 truncate">
                  <span className="font-medium">{u.uploaded_by_name || "Someone"}</span>{" "}
                  uploaded <span className="font-mono text-xs">{u.filename || "data"}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  {u.program_name} · {u.row_count} rows · status <span className="font-mono">{u.status}</span>
                </p>
              </div>
              <span className="text-[11px] text-slate-500 shrink-0">
                {u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : "—"}
              </span>
            </li>
          ))}
          {(ops.recent_activity || []).length === 0 && (
            <p className="text-xs text-slate-500 py-3">No uploads yet.</p>
          )}
        </ul>
      )}
    </div>
  );
}

function UpcomingTasks({ tasks }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="v2-upcoming-tasks">
      <div className="flex items-center justify-between">
        <p className="label-overline flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5 text-emerald-600" /> Upcoming tasks
        </p>
        {tasks && (
          <span className="text-[10px] text-slate-500" data-testid="v2-tasks-count">
            {tasks.tasks?.length || 0} open
          </span>
        )}
      </div>
      {!tasks ? (
        <div className="mt-3 space-y-2">
          <div className="h-3 bg-slate-100 rounded-sm w-3/4 animate-pulse" />
          <div className="h-3 bg-slate-100 rounded-sm w-1/2 animate-pulse" />
        </div>
      ) : (tasks.tasks || []).length === 0 ? (
        <p className="text-xs text-slate-500 mt-3" data-testid="v2-tasks-empty">All caught up — no open tasks.</p>
      ) : (
        <ul className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="v2-tasks-list">
          {tasks.tasks.slice(0, 12).map((t) => (
            <li
              key={t.id}
              className="text-sm border border-slate-100 rounded-sm p-2.5 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors"
              data-testid={`v2-task-${t.id}`}
            >
              <div className="flex items-start gap-2">
                <span className={`text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-sm border shrink-0 ${PRIORITY_CHIP[t.priority] || PRIORITY_CHIP.low}`}>
                  {t.priority}
                </span>
                <div className="min-w-0 flex-1">
                  <Link to={t.link} className="text-slate-900 font-medium hover:text-emerald-700 line-clamp-2">
                    {t.title}
                  </Link>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {t.program_name} · due {t.deadline}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomizeKpiDialog({ open, onClose, picker, selected, onSave }) {
  const [draft, setDraft] = useState([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (open) setDraft(selected.slice(0, 4));
  }, [open, selected]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return picker;
    return picker.filter((i) =>
      (i.code || "").toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q),
    );
  }, [picker, query]);

  const toggle = (iid) => {
    setDraft((d) => {
      if (d.includes(iid)) return d.filter((x) => x !== iid);
      if (d.length >= 4) {
        toast.warning("You can pin up to 4 indicators. Remove one first.");
        return d;
      }
      return [...d, iid];
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="v2-customize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-emerald-600" /> Customize Row 2
          </DialogTitle>
          <DialogDescription>
            Pin up to 4 numeric indicators. Each gets its own KPI card on your dashboard.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by code or name…"
          className="mt-3"
          data-testid="v2-customize-search"
        />
        <p className="text-xs text-slate-500 mt-2">
          {draft.length} of 4 pinned · {filtered.length} indicators visible
        </p>
        <ul className="mt-3 divide-y divide-slate-100 max-h-[50vh] overflow-y-auto pr-1" data-testid="v2-customize-list">
          {filtered.slice(0, 100).map((i) => {
            const checked = draft.includes(i.id);
            return (
              <li
                key={i.id}
                className="py-2 flex items-center gap-3 cursor-pointer"
                onClick={() => toggle(i.id)}
                data-testid={`v2-customize-row-${i.id}`}
              >
                <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs text-slate-700">{i.code}</p>
                  <p className="text-xs text-slate-500 truncate">{i.name}</p>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} data-testid="v2-customize-cancel">Cancel</Button>
          <Button
            onClick={() => onSave(draft)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="v2-customize-save"
          >
            Save selection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareDashboardDialog({ open, onClose, selPrograms, pinned }) {
  const [label, setLabel] = useState("");
  const [expiresIn, setExpiresIn] = useState(30);
  const [busy, setBusy] = useState(false);
  const [shares, setShares] = useState([]);
  const [generated, setGenerated] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/dashboard/v2/shares");
      setShares(data.shares || []);
    } catch { /* not authorised — ignore */ }
  };
  useEffect(() => { if (open) reload(); }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dashboard/v2/shares", {
        label: label || "Dashboard share",
        program_ids: selPrograms?.length ? selPrograms : null,
        pinned_indicator_ids: pinned?.length ? pinned : null,
        expires_in_days: expiresIn || null,
      });
      const fullUrl = `${window.location.origin}/dashboard/share/${data.token}`;
      setGenerated({ token: data.token, url: fullUrl });
      toast.success("Share link generated");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create share");
    } finally { setBusy(false); }
  };
  const copy = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  const revoke = async (token) => {
    if (!window.confirm("Revoke this share? The link will stop working immediately.")) return;
    try {
      await api.delete(`/dashboard/v2/shares/${token}`);
      toast.success("Share revoked");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't revoke");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setGenerated(null); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="v2-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-emerald-600" /> Share dashboard
          </DialogTitle>
          <DialogDescription>
            View-only public link that bakes the current scope + pinned indicators in. Revoke anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Label (private)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Q1 board readout" className="mt-1" data-testid="v2-share-label" />
          </div>
          <div>
            <Label className="label-overline">Expires in (days, 0 = never)</Label>
            <Input type="number" min="0" max="365" value={expiresIn}
              onChange={(e) => setExpiresIn(Number(e.target.value) || 0)} className="mt-1 w-32" data-testid="v2-share-expiry" />
          </div>
          <Button onClick={create} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="v2-share-create-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            Generate link
          </Button>
          {generated && (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3" data-testid="v2-share-generated">
              <p className="label-overline !text-emerald-700">New link</p>
              <div className="flex items-center gap-2 mt-2">
                <Input readOnly value={generated.url} className="font-mono text-xs" data-testid="v2-share-url" />
                <Button size="sm" variant="outline" onClick={() => copy(generated.url)} data-testid="v2-share-copy">Copy</Button>
              </div>
            </div>
          )}
          {shares.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="label-overline mb-2">Active shares</p>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="v2-share-list">
                {shares.filter((s) => !s.revoked).map((s) => (
                  <li key={s.token} className="flex items-center gap-2 text-xs" data-testid={`v2-share-row-${s.token}`}>
                    <span className="flex-1 min-w-0 truncate text-slate-700 font-medium">{s.label}</span>
                    <span className="text-slate-500 font-mono">{s.view_count || 0} views</span>
                    <button onClick={() => copy(`${window.location.origin}/dashboard/share/${s.token}`)} className="text-slate-500 hover:text-slate-900" data-testid={`v2-share-row-copy-${s.token}`}>Copy</button>
                    <button onClick={() => revoke(s.token)} className="text-rose-600 hover:text-rose-700" data-testid={`v2-share-row-revoke-${s.token}`}>Revoke</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// SmartReportDialog — Phase 3.22. Format picker (PDF / PNG / PPTX) + download.
// ---------------------------------------------------------------------------
const FORMATS = [
  { key: "pdf", label: "PDF (one-pager)", blurb: "A4 landscape · KPI grid + AI brief + footer share link.", icon: FileDown },
  { key: "png", label: "PNG (snapshot)", blurb: "1600×900 single-frame snapshot for Slack / email.", icon: FileDown },
  { key: "pptx", label: "PowerPoint (slides)", blurb: "Cover + KPIs + AI + one slide per active widget.", icon: FileDown },
];

function SmartReportDialog({ open, onClose, selPrograms, pinned, activeWidgets }) {
  const [fmt, setFmt] = useState("pdf");
  const [shareToken, setShareToken] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const widgetKeys = Object.entries(activeWidgets || {}).filter(([, v]) => v).map(([k]) => k);
      const { data, headers } = await api.post("/dashboard/v2/export", {
        format: fmt,
        program_ids: selPrograms?.length ? selPrograms : null,
        pinned_indicator_ids: pinned?.length ? pinned : null,
        share_token: shareToken || null,
        active_widgets: widgetKeys.length ? widgetKeys : null,
      }, { responseType: "blob" });
      // Best-effort filename from Content-Disposition; otherwise default.
      let filename = `dashboard.${fmt}`;
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); a.remove();
      toast.success(`${fmt.toUpperCase()} downloaded`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="v2-smart-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-4 h-4 text-emerald-600" /> Smart report
          </DialogTitle>
          <DialogDescription>
            Server-rendered snapshot of the current dashboard scope. Includes org logo header + share-link footer.
          </DialogDescription>
        </DialogHeader>
        <ul className="mt-2 space-y-2" data-testid="v2-smart-report-format-list">
          {FORMATS.map((f) => {
            const Icon = f.icon;
            const on = fmt === f.key;
            return (
              <li
                key={f.key}
                onClick={() => setFmt(f.key)}
                className={`border rounded-sm p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                  on ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
                data-testid={`v2-smart-report-fmt-${f.key}`}
              >
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${on ? "text-emerald-600" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{f.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{f.blurb}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${on ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                  {on && <div className="w-full h-full rounded-full bg-white scale-50" />}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 space-y-1.5">
          <Label className="label-overline">Share-link footer (optional token)</Label>
          <Input
            value={shareToken}
            onChange={(e) => setShareToken(e.target.value)}
            placeholder="Paste a dashboard share token to embed it in the footer"
            data-testid="v2-smart-report-share-token"
            className="font-mono text-xs"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="v2-smart-report-cancel">Cancel</Button>
          <Button onClick={download} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="v2-smart-report-download">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
            {busy ? "Generating..." : "Download"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Layer 2 — chart primitives (Phase 3.27).
//
// Both reuse the existing /dashboard/insights payload, so there's no new
// backend route and we stay aligned with the rest of the filter state.
//
// IndicatorBarChartCard: top 5 indicators by latest value.
// DistributionPieChartCard: first non-period disaggregation axis (gender,
//   region, age_group, etc.) — falls back to a friendly empty state.
// ---------------------------------------------------------------------------
function ChartFrame({ title, subtitle, children, testid, height = 200 }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-4 h-full flex flex-col"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <p className="font-heading text-sm font-medium text-slate-900 truncate">{title}</p>
        {subtitle && <p className="text-[11px] text-slate-500 shrink-0 truncate">{subtitle}</p>}
      </div>
      <div className="flex-1" style={{ minHeight: height }}>
        {children}
      </div>
    </div>
  );
}

function useInsightsForCharts(programIds) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.post("/dashboard/insights", {
      program_ids: programIds?.length ? programIds : null,
    })
      .then(({ data }) => { if (live) setData(data); })
      .catch(() => { if (live) setData(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [programIds]);
  return { data, loading };
}

const PIE_COLORS = ["#10b981", "#0f172a", "#64748b", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#06b6d4"];

export function IndicatorBarChartCard({ programIds }) {
  const { data, loading } = useInsightsForCharts(programIds);
  const tiles = (data?.kpi_tiles || [])
    .filter((t) => Number(t?.latest_value || 0) > 0)
    .slice(0, 5)
    .map((t) => ({
      code: t.code,
      name: t.name,
      value: Number(t.latest_value || 0),
    }));
  return (
    <ChartFrame title="Indicators" subtitle={tiles.length ? `Top ${tiles.length} by latest value` : ""} testid="layer-2-bar-card" height={180}>
      {loading && !tiles.length ? (
        <div className="h-full grid place-items-center text-xs text-slate-400">Loading…</div>
      ) : tiles.length === 0 ? (
        <div className="h-full grid place-items-center text-xs text-slate-400 text-center px-3">No indicators with a latest value in this slice.</div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={tiles} layout="vertical" margin={{ top: 6, right: 12, left: 6, bottom: 6 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis
              type="category"
              dataKey="code"
              tick={{ fontSize: 10, fill: "#0f172a" }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              width={70}
            />
            <Tooltip
              cursor={{ fill: "rgba(16,185,129,0.06)" }}
              contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }}
              formatter={(v, _k, item) => [Number(v).toLocaleString(), item?.payload?.name || "value"]}
              labelFormatter={() => ""}
            />
            <Bar dataKey="value" fill="#10b981" radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}

export function DistributionPieChartCard({ programIds }) {
  const { data, loading } = useInsightsForCharts(programIds);
  const axes = data?.charts?.axes || [];
  // Prefer gender / sex if available, else first axis with at least one slice
  const preferred = axes.find((a) => /^(gender|sex)$/i.test(a.axis || "")) ||
                    axes.find((a) => (a.slices || []).length > 0);
  const slices = (preferred?.slices || []).slice(0, 6).map((s) => ({
    name: s.label,
    value: Number(s.value || 0),
  }));
  const total = slices.reduce((a, b) => a + b.value, 0);
  return (
    <ChartFrame
      title={preferred ? `Distribution · ${preferred.axis}` : "Distribution"}
      subtitle={total ? `${total.toLocaleString()} total` : ""}
      testid="layer-2-pie-card"
      height={180}
    >
      {loading && !slices.length ? (
        <div className="h-full grid place-items-center text-xs text-slate-400">Loading…</div>
      ) : !slices.length ? (
        <div className="h-full grid place-items-center text-xs text-slate-400 text-center px-3">No disaggregation axes in this slice yet.</div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius={36}
              outerRadius={64}
              paddingAngle={1}
              stroke="#fff"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
              style={{ fontSize: 9 }}
            >
              {slices.map((_, idx) => (
                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }}
              formatter={(v) => Number(v).toLocaleString()}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}

